import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, AlertTriangle, Lightbulb, Calendar, Target, Sparkles,
  FileText, CheckCircle, Clock, AlertCircle, TrendingUp, Search,
  Filter, ChevronDown, ExternalLink, Zap, BarChart3, RefreshCw,
  ArrowRight, Plus, Download, Trash2, Eye
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { ScoreRing } from '../ui/ScoreRing';
import { PipelineStrip, PipelineStat } from '../ui/PipelineStrip';
import { getPipelineStages, setContractStage, STAGE_META, type PipelineStage } from '../../lib/pipeline-service';

// Lifecycle stages relevant to a generated proposal, with proposal-friendly labels.
const PROPOSAL_STAGES: { value: PipelineStage; label: string }[] = [
  { value: 'pursuing', label: 'Planning' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'won', label: 'Awarded' },
  { value: 'lost', label: 'Not Awarded' },
];
const stageLabel = (s: PipelineStage): string =>
  PROPOSAL_STAGES.find((p) => p.value === s)?.label || STAGE_META[s]?.label || s;

interface ProposalTemplate {
  id: string;
  template_name: string;
  notice_id: string;
  content_json: any;
  template_url: string;
  proposal_type: 'basic' | 'enhanced' | 'source-grounded';
  enhanced_data?: any;
  created_at: string;
  updated_at: string;
  contract?: any;
}

type StatusFilter = 'all' | 'ready' | 'needs-attention' | 'in-progress' | 'critical';
type SortField = 'title' | 'agency' | 'dueDate' | 'score' | 'updated';
type SortDirection = 'asc' | 'desc';

export function WorkspaceLanding() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<ProposalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [stageFilter, setStageFilter] = useState<'all' | PipelineStage>('all');
  const [stages, setStages] = useState<Map<string, PipelineStage>>(new Map());
  const [sortField, setSortField] = useState<SortField>('updated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadProposals();
  }, [profile]);

  const loadProposals = async () => {
    try {
      if (!profile) return;

      const { data: templateData, error: templateError } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false });

      if (templateError) throw templateError;

      const validTemplates = templateData?.filter(template => template.notice_id) || [];

      // Load contract details for each template
      const contractPromises = validTemplates.map(async (template) => {
        let { data: contract } = await supabase
          .from('sam_contracts')
          .select('*')
          .eq('notice_id', template.notice_id)
          .maybeSingle();

        if (!contract) {
          const result = await supabase
            .from('sam_contracts')
            .select('*')
            .eq('id', template.notice_id)
            .maybeSingle();
          contract = result.data;
        }

        return { template, contract };
      });

      const results = await Promise.all(contractPromises);

      const combinedData = results
        .filter(r => r.contract)
        .map(r => {
          let proposalType: 'basic' | 'enhanced' | 'source-grounded' = 'basic';
          if (
            r.template.content_json?.proposalType === 'source-grounded' ||
            r.template.content_json?.sourceGroundedOutput ||
            r.template.template_name?.includes('Source-Grounded')
          ) {
            proposalType = 'source-grounded';
          } else if (
            r.template.proposal_type === 'enhanced' ||
            r.template.content_json?.contractAnalysis ||
            r.template.template_name?.includes('Enhanced')
          ) {
            proposalType = 'enhanced';
          }

          return {
            ...r.template,
            proposal_type: proposalType,
            contract: r.contract,
          };
        });

      setProposals(combinedData);

      // Load lifecycle (pipeline) stages for status management/filtering.
      const ids = [...new Set(combinedData.map((p) => p.contract?.notice_id || p.notice_id).filter(Boolean))];
      if (profile.id && ids.length) {
        setStages(await getPipelineStages(profile.id, ids));
      }
    } catch (error) {
      console.error('Error loading proposals:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  // Key used by the pipeline (contract notice id).
  const noticeKey = (p: ProposalTemplate): string => p.contract?.notice_id || p.notice_id;

  // Set/clear a proposal's lifecycle stage (optimistic).
  const handleSetStage = async (p: ProposalTemplate, stage: PipelineStage | '') => {
    if (!profile?.id) return;
    const key = noticeKey(p);
    setStages((prev) => {
      const next = new Map(prev);
      if (stage) next.set(key, stage); else next.delete(key);
      return next;
    });
    try {
      await setContractStage(profile.id, key, stage || null);
    } catch (e) {
      console.error('Failed to set proposal stage:', e);
      toast.error('Could not update status');
    }
  };

  // Calculate proposal status and score
  const getProposalStatus = (proposal: ProposalTemplate): { status: string; color: string; icon: React.ReactNode } => {
    const content = proposal.content_json;
    if (!content) {
      return { status: 'In Progress', color: 'blue', icon: <Clock className="h-4 w-4" /> };
    }

    const hasCoverLetter = content.coverLetter || content.executiveSummary;
    const hasSections = content.technicalSections?.length > 0 || content.technicalApproach;
    const hasGaps = content.gaps?.length > 0 || content.sourceGroundedOutput?.gapAnalysis?.criticalGaps?.length > 0;
    const hasCriticalGaps = content.sourceGroundedOutput?.gapAnalysis?.criticalGaps?.length > 0;

    if (hasCriticalGaps) {
      return { status: 'Critical Gaps', color: 'red', icon: <AlertCircle className="h-4 w-4" /> };
    }
    if (hasGaps) {
      return { status: 'Needs Attention', color: 'yellow', icon: <AlertTriangle className="h-4 w-4" /> };
    }
    if (hasCoverLetter && hasSections) {
      return { status: 'Ready', color: 'green', icon: <CheckCircle className="h-4 w-4" /> };
    }
    return { status: 'In Progress', color: 'blue', icon: <Clock className="h-4 w-4" /> };
  };

  const calculateProposalScore = (proposal: ProposalTemplate): number => {
    const content = proposal.content_json;
    if (!content) return 0;

    // Prefer the REAL Red Team submitability score once the proposal has been reviewed —
    // it's the authoritative quality signal. Fall back to the content-presence heuristic
    // for proposals that haven't been Red-Teamed yet.
    const rt =
      content.redTeamAssessment?.current?.submitabilityScore ??
      content.winAssessment?.submitabilityScore ??
      content.sourceGroundedOutput?.winAssessment?.submitabilityScore;
    if (typeof rt === 'number' && isFinite(rt)) return Math.max(0, Math.min(100, Math.round(rt)));

    let score = 0;

    // Base score for having content
    if (content.coverLetter || content.executiveSummary) score += 20;
    if (content.technicalSections?.length > 0) score += 20;
    if (content.contractAnalysis) score += 15;
    if (content.profileAnalysis) score += 10;

    // Bonus for advanced proposal types
    if (proposal.proposal_type === 'source-grounded') score += 20;
    else if (proposal.proposal_type === 'enhanced') score += 10;

    // Deduct for gaps
    const criticalGaps = content.sourceGroundedOutput?.gapAnalysis?.criticalGaps?.length || 0;
    const improvementGaps = content.sourceGroundedOutput?.gapAnalysis?.improvementOpportunities?.length || 0;
    score -= criticalGaps * 10;
    score -= improvementGaps * 3;

    // Add for sections
    const sectionCount = content.technicalSections?.length || 0;
    score += Math.min(sectionCount * 3, 15);

    return Math.max(0, Math.min(100, score));
  };

  const getSectionProgress = (proposal: ProposalTemplate): { completed: number; total: number } => {
    const content = proposal.content_json;
    if (!content) return { completed: 0, total: 8 };

    const sections = [
      'coverLetter', 'executiveSummary', 'contractAnalysis', 'profileAnalysis',
      'technicalApproach', 'managementApproach', 'pastPerformance', 'pricing'
    ];

    let completed = 0;
    sections.forEach(section => {
      if (content[section]) completed++;
    });

    // Also count technical sections
    if (content.technicalSections?.length > 0) {
      completed += Math.min(content.technicalSections.length, 4);
    }

    return { completed, total: 8 };
  };

  // Filter and sort proposals
  const filteredProposals = useMemo(() => {
    let filtered = [...proposals];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.contract?.title?.toLowerCase().includes(query) ||
        p.contract?.agency?.toLowerCase().includes(query) ||
        p.template_name?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => {
        const { status } = getProposalStatus(p);
        switch (statusFilter) {
          case 'ready': return status === 'Ready';
          case 'needs-attention': return status === 'Needs Attention';
          case 'in-progress': return status === 'In Progress';
          case 'critical': return status === 'Critical Gaps';
          default: return true;
        }
      });
    }

    // Lifecycle (pipeline) stage filter
    if (stageFilter !== 'all') {
      filtered = filtered.filter(p => stages.get(noticeKey(p)) === stageFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'title':
          comparison = (a.contract?.title || '').localeCompare(b.contract?.title || '');
          break;
        case 'agency':
          comparison = (a.contract?.agency || '').localeCompare(b.contract?.agency || '');
          break;
        case 'dueDate':
          comparison = new Date(a.contract?.response_deadline || 0).getTime() -
                       new Date(b.contract?.response_deadline || 0).getTime();
          break;
        case 'score':
          comparison = calculateProposalScore(a) - calculateProposalScore(b);
          break;
        case 'updated':
          comparison = new Date(a.updated_at || a.created_at).getTime() -
                       new Date(b.updated_at || b.created_at).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [proposals, searchQuery, statusFilter, stageFilter, stages, sortField, sortDirection]);

  // Calculate stats
  const stats = useMemo(() => {
    const ready = proposals.filter(p => getProposalStatus(p).status === 'Ready').length;
    const needsAttention = proposals.filter(p => getProposalStatus(p).status === 'Needs Attention').length;
    const inProgress = proposals.filter(p => getProposalStatus(p).status === 'In Progress').length;
    const critical = proposals.filter(p => getProposalStatus(p).status === 'Critical Gaps').length;
    const avgScore = proposals.length > 0
      ? Math.round(proposals.reduce((sum, p) => sum + calculateProposalScore(p), 0) / proposals.length)
      : 0;

    return { ready, needsAttention, inProgress, critical, avgScore, total: proposals.length };
  }, [proposals]);

  // AI Insights
  const aiInsights = useMemo(() => {
    const insights: { icon: React.ReactNode; message: string; type: 'warning' | 'info' | 'success' | 'action' }[] = [];

    const criticalCount = stats.critical;
    const needsAttentionCount = stats.needsAttention;
    const dueSoon = proposals.filter(p => {
      const dueDate = p.contract?.response_deadline;
      if (!dueDate) return false;
      const days = Math.ceil((new Date(dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      return days > 0 && days <= 7;
    }).length;

    if (criticalCount > 0) {
      insights.push({
        icon: <AlertCircle className="h-5 w-5 text-urgent" />,
        message: `${criticalCount} proposal${criticalCount > 1 ? 's have' : ' has'} critical gaps that need immediate attention`,
        type: 'warning'
      });
    }

    if (needsAttentionCount > 0) {
      insights.push({
        icon: <AlertTriangle className="h-5 w-5 text-fit-moderate" />,
        message: `${needsAttentionCount} proposal${needsAttentionCount > 1 ? 's' : ''} could score higher with AI improvements`,
        type: 'info'
      });
    }

    if (dueSoon > 0) {
      insights.push({
        icon: <Calendar className="h-5 w-5 text-bw-primary" />,
        message: `${dueSoon} proposal${dueSoon > 1 ? 's' : ''} due within 7 days`,
        type: 'info'
      });
    }

    if (stats.ready > 0 && stats.ready === stats.total) {
      insights.push({
        icon: <CheckCircle className="h-5 w-5 text-success" />,
        message: 'All proposals are ready for submission!',
        type: 'success'
      });
    }

    if (insights.length === 0 && stats.total > 0) {
      insights.push({
        icon: <Sparkles className="h-5 w-5 text-bw-primary" />,
        message: 'Your proposal portfolio is looking good! Keep up the great work.',
        type: 'success'
      });
    }

    return insights;
  }, [proposals, stats]);

  const handleDeleteProposal = async (proposalId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this proposal? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('proposal_templates')
        .delete()
        .eq('id', proposalId);

      if (error) throw error;

      setProposals(prev => prev.filter(p => p.id !== proposalId));
      toast.success('Proposal deleted successfully');
    } catch (error) {
      console.error('Error deleting proposal:', error);
      toast.error('Failed to delete proposal');
    }
  };

  if (loading) {
    return (
      <div className="py-8 space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-bw-surface-subtle border border-bw-border rounded-xl mb-6"></div>
          <div className="h-24 bg-bw-surface-subtle border border-bw-border rounded-xl mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-bw-surface-subtle border border-bw-border rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-bw-ink tracking-tight flex items-center">
            <Briefcase className="h-6 w-6 mr-2.5 text-bw-primary" />
            Proposal Workspace
          </h1>
          <p className="text-sm text-bw-muted mt-0.5">AI-grounded proposal studio</p>
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg text-sm font-semibold hover:brightness-110 transition-all"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Proposal
        </button>
      </div>

      {/* Pipeline strip — replaces the six stat cards */}
      <PipelineStrip
        stats={[
          { value: stats.total, label: 'Total' },
          { value: stats.ready, label: 'Ready' },
          { value: stats.inProgress, label: 'In progress' },
          { value: stats.needsAttention + stats.critical, label: 'Needs attention', urgent: stats.needsAttention + stats.critical > 0 },
          { value: `${stats.avgScore}%`, label: 'Avg score' },
        ] as PipelineStat[]}
        label="Portfolio"
      />

      {/* AI Insights Banner */}
      {aiInsights.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border p-5">
          <div className="flex items-center mb-3">
            <Sparkles className="h-4 w-4 text-bw-primary mr-2" />
            <h3 className="text-[15px] font-semibold text-bw-ink">AI Insights</h3>
          </div>
          <div className="space-y-2.5">
            {aiInsights.map((insight, idx) => (
              <div
                key={idx}
                className={`flex items-center p-3 rounded-lg border ${
                  insight.type === 'warning' ? 'bg-urgent-bg border-urgent-border' :
                  insight.type === 'success' ? 'bg-success-bg border-success-border' :
                  'bg-bw-primary-tint border-bw-primary/20'
                }`}
              >
                {insight.icon}
                <span className="ml-3 text-[13px] text-bw-ink">{insight.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bw-muted" />
            <input
              type="text"
              placeholder="Search proposals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-bw-border rounded-lg bg-bw-surface text-bw-ink focus:ring-2 focus:ring-bw-primary focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-bw-muted" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="px-3 py-2 border border-bw-border rounded-lg bg-bw-surface text-bw-ink focus:ring-2 focus:ring-bw-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="ready">Ready</option>
              <option value="needs-attention">Needs Attention</option>
              <option value="in-progress">In Progress</option>
              <option value="critical">Critical Gaps</option>
            </select>
            {/* Lifecycle (pipeline) stage filter */}
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value as 'all' | PipelineStage)}
              className="px-3 py-2 border border-bw-border rounded-lg bg-bw-surface text-bw-ink focus:ring-2 focus:ring-bw-primary focus:border-transparent"
              title="Filter by submission stage"
            >
              <option value="all">All Stages</option>
              {PROPOSAL_STAGES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value as SortField)}
              className="px-3 py-2 border border-bw-border rounded-lg bg-bw-surface text-bw-ink focus:ring-2 focus:ring-bw-primary focus:border-transparent"
            >
              <option value="updated">Last Updated</option>
              <option value="title">Title</option>
              <option value="agency">Agency</option>
              <option value="dueDate">Due Date</option>
              <option value="score">AI Score</option>
            </select>
            <button
              onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-bw-border rounded-lg hover:bg-bw-surface-subtle text-bw-muted"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Proposal List */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        {filteredProposals.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="h-16 w-16 text-bw-border mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-bw-ink mb-2">
              {proposals.length === 0 ? 'No proposals yet' : 'No matching proposals'}
            </h3>
            <p className="text-bw-muted mb-6">
              {proposals.length === 0
                ? 'Generate proposals from contracts in the Dashboard'
                : 'Try adjusting your search or filters'
              }
            </p>
            {proposals.length === 0 && (
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg text-sm font-semibold hover:brightness-110"
              >
                <Plus className="h-4 w-4 mr-2" />
                Find Contracts
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-bw-border">
            {filteredProposals.map((proposal) => {
              const { status, color, icon } = getProposalStatus(proposal);
              const score = calculateProposalScore(proposal);
              const progress = getSectionProgress(proposal);
              const dueDate = proposal.contract?.response_deadline;
              const daysUntilDue = dueDate
                ? Math.ceil((new Date(dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                : null;
              const content = proposal.content_json || {};
              const gaps =
                (content.gaps?.length || 0) +
                (content.sourceGroundedOutput?.gapAnalysis?.criticalGaps?.length || 0);
              const statusPill =
                color === 'green' ? 'bg-success-bg text-success border-success-border' :
                color === 'yellow' ? 'bg-fit-moderate-bg text-fit-moderate border-fit-moderate-border' :
                color === 'red' ? 'bg-urgent-bg text-urgent border-urgent-border' :
                'bg-bw-primary-tint text-bw-primary border-bw-primary/20';
              const typeLabel = proposal.proposal_type === 'source-grounded' ? 'Source-Grounded' :
                proposal.proposal_type === 'enhanced' ? 'Enhanced' : 'Basic';

              return (
                <div
                  key={proposal.id}
                  onClick={() => navigate(`/workspace/${proposal.id}`)}
                  className="flex items-center gap-4 p-4 hover:bg-bw-surface-subtle cursor-pointer transition-colors group"
                >
                  {/* Score ring */}
                  <ScoreRing score={score} size={52} />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold border ${statusPill}`}>
                        {icon}
                        {status}
                      </span>
                      <span className="text-[11px] text-bw-muted">
                        Updated {new Date(proposal.updated_at || proposal.created_at).toLocaleDateString()}
                      </span>
                      {daysUntilDue !== null && daysUntilDue > 0 && daysUntilDue <= 5 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-urgent-bg text-urgent border border-urgent-border">
                          <Calendar className="h-3 w-3" />
                          {daysUntilDue}d left
                        </span>
                      )}
                    </div>
                    <h3 className="text-[14px] font-bold text-bw-ink truncate group-hover:text-bw-primary transition-colors">
                      {proposal.contract?.title || proposal.template_name}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-bw-muted flex-wrap">
                      <span>{proposal.contract?.agency || typeLabel}</span>
                      <span className="inline-flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {progress.completed}/{progress.total} sections
                      </span>
                      <span className={`font-semibold ${gaps === 0 ? 'text-success' : 'text-fit-moderate'}`}>
                        {gaps === 0 ? 'All grounded · 0 gaps' : `${gaps} gap${gaps > 1 ? 's' : ''} flagged`}
                      </span>
                    </div>
                  </div>

                  {/* Lifecycle status — manage submission stage (Planning → Submitted → Awarded/Not Awarded) */}
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const cur = stages.get(noticeKey(proposal));
                      return (
                        <select
                          value={cur || ''}
                          onChange={(e) => handleSetStage(proposal, e.target.value as PipelineStage | '')}
                          title="Submission status"
                          className={`text-[11px] font-semibold rounded-full border px-2 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-bw-primary/30 ${
                            cur ? STAGE_META[cur].pill : 'bg-bw-surface-subtle text-bw-muted border-bw-border'
                          }`}
                        >
                          <option value="">Set status…</option>
                          {PROPOSAL_STAGES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>

                  {/* Actions + chevron */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/workspace/${proposal.id}`);
                      }}
                      className="p-2 text-bw-muted hover:text-bw-primary hover:bg-bw-primary-tint rounded-lg transition-colors"
                      title="Open Workspace"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteProposal(proposal.id, e)}
                      className="p-2 text-bw-muted hover:text-urgent hover:bg-urgent-bg rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                  <ArrowRight className="h-5 w-5 text-bw-muted group-hover:text-bw-primary transition-colors flex-shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {stats.needsAttention + stats.critical > 0 && (
        <div className="bg-bw-sidebar rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[15px] font-semibold mb-1">Fix All Issues with AI</h3>
              <p className="text-white/70 text-sm">
                Let AI remediate {stats.needsAttention + stats.critical} proposal{stats.needsAttention + stats.critical > 1 ? 's' : ''} with detected issues
              </p>
            </div>
            <button className="px-5 py-2.5 bg-bw-surface text-bw-primary rounded-lg text-sm font-semibold hover:bg-bw-surface-subtle transition-colors flex items-center flex-shrink-0">
              <Zap className="h-4 w-4 mr-2" />
              Fix All Issues
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
